/*
 * Free gift: adds the gift line once the cart reaches the spend in the side
 * cart settings, and takes it out again when the cart drops below it.
 *
 * The side cart section works out whether the cart qualifies and lists the
 * gift lines already in it. This script only reads that and makes the cart
 * match. Its own requests carry omd_gift, so they never trigger it again.
 *
 * Shopify keeps only one of two cart writes that are in flight at once. So
 * this engine and the offer stamping engine (offer-test-stamps.js) take turns
 * through one shared lock, and this one waits for the stamps to settle first.
 */
(function (root, factory) {
  // Same lock as offer-test-stamps.js; whichever loads first creates it.
  function cartLock(win) {
    // Shopify keeps only one of two cart writes in flight at once, so the
    // lock is never let go while its holder has a cart request in flight.
    // HOLD_CEILING only lets go of a holder that is waiting on nothing. A
    // holder that was let go starts no new request; its engine runs again
    // later and reads the cart before any write.
    //
    // A request still in flight after REQUEST_CEILING keeps the lock too.
    // Instead, everyone waiting gives up and runs nothing, and so does anyone
    // who asks while it is still in flight. The error they get carries
    // `settled`, which resolves once that request is done.
    var HOLD_CEILING = 15000;
    var REQUEST_CEILING = 30000;
    if (!win.omdCartLock) {
      var tail = Promise.resolve();
      var waiting = [];
      // Requests in flight past REQUEST_CEILING, and when they are all done.
      var stuck = 0;
      var unstick = null;
      var settled = null;
      win.omdCartLock = function (task) {
        var release;
        var released = new Promise(function (resolve) { release = resolve; });
        var stop;
        var stopped = new Promise(function (resolve, reject) { stop = reject; });
        var gaveUp = false;
        var giveUp = function () {
          if (gaveUp) return;
          gaveUp = true;
          waiting.splice(waiting.indexOf(giveUp), 1);
          var error = new Error('cart request hung');
          error.gaveUp = true;
          error.settled = settled;
          stop(error);
        };
        waiting.push(giveUp);
        var turn = tail.then(function () {
          // Gave up while waiting: pass the turn straight on.
          if (gaveUp) return release();
          waiting.splice(waiting.indexOf(giveUp), 1);
          var busy = 0;
          var overdue = false;
          var ceiling = null;
          var holder = { gone: false };
          var letGo = function () {
            if (holder.gone) return;
            holder.gone = true;
            win.clearTimeout(ceiling);
            release();
          };
          var track = function (start, timed) {
            if (holder.gone) {
              var error = new Error('cart lock let go');
              error.released = true;
              return Promise.reject(error);
            }
            busy += 1;
            var late = false;
            var hung = timed ? win.setTimeout(function () {
              late = true;
              if (!stuck++) settled = new Promise(function (resolve) { unstick = resolve; });
              waiting.slice().forEach(function (quit) { quit(); });
            }, REQUEST_CEILING) : null;
            var settle = function () {
              if (hung !== null) win.clearTimeout(hung);
              if (late && !--stuck) unstick();
              busy -= 1;
              if (overdue && !busy) letGo();
            };
            var work = Promise.resolve().then(start);
            work.then(settle, settle);
            return work;
          };
          // One cart request.
          holder.request = function (start) { return track(start, true); };
          // Requests that belong together, e.g. a split's lower and add.
          holder.group = function (start) { return track(start, false); };
          ceiling = win.setTimeout(function () {
            overdue = true;
            if (!busy) letGo();
          }, HOLD_CEILING);
          var work = Promise.resolve().then(function () { return task(holder); });
          // A task that ends with a request still in flight keeps the lock
          // until that request is done.
          var finish = function () {
            if (busy) overdue = true;
            else letGo();
          };
          work.then(finish, finish);
          return work;
        });
        tail = released;
        if (stuck) giveUp();
        return Promise.race([turn, stopped]);
      };
    }
    return win.omdCartLock;
  }
  factory.cartLock = cartLock;

  if (typeof module === 'object' && module.exports) module.exports = factory;
  else root.OmdFreeGift = factory;

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var start = function () {
    var drawer = document.querySelector('side-cart-drawer');
    if (!drawer || !drawer.querySelector('script[data-free-gift]')) return;
    var wrapper = drawer.closest('.shopify-section');
    var engine = factory({
      fetch: window.fetch.bind(window),
      root: (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/',
      section: wrapper ? wrapper.id.replace('shopify-section-', '') : 'side-cart',
      setTimeout: window.setTimeout.bind(window),
      clearTimeout: window.clearTimeout.bind(window),
      lock: cartLock(window),
      // Read when needed: the stamping engine may start after this one.
      stamps: function () { return window.omdOfferStamps; },
      refreshCart: function () {
        if (window.sideCart && window.sideCart.refresh) return window.sideCart.refresh();
        return null;
      }
    });
    window.omdFreeGift = engine;
    engine.bindPage(window, document);
    if (/(?:^|; )cart=/.test(document.cookie)) engine.schedule();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})(typeof self !== 'undefined' ? self : this, function createFreeGift(env) {
  var DEBOUNCE = 400;
  // Longest wait for the stamps to settle. Past it the gift goes ahead; the
  // lock still keeps the two engines' writes apart.
  var STAMPS_WAIT = 10000;
  var MARKER = 'omd_gift';
  var PROPERTY = '_Gifted';

  var running = null;
  var rerun = false;
  var timer = null;
  var lock = env.lock || function (task) { return Promise.resolve().then(task); };
  // The cart lock's holder for the pass under way, if any.
  var holder = null;

  function url(path) {
    return env.root + path + (path.indexOf('?') === -1 ? '?' : '&') + MARKER + '=1';
  }

  function json(response) {
    return response.json().then(function (body) {
      if (!response.ok) throw new Error(body.description || body.message || 'cart request failed');
      return body;
    });
  }

  // Every cart request of a pass goes through the lock's holder, so the
  // lock knows what is in flight. Without a lock it just runs.
  function request(start) {
    return holder ? holder.request(start) : start();
  }

  function post(path, body) {
    return request(function () {
      return env.fetch(url(path), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(json);
    });
  }

  function readState() {
    return request(function () {
      return env.fetch(url('?sections=' + env.section), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      }).then(json);
    })
      .then(function (sections) {
        var match = /<script[^>]*data-free-gift[^>]*>([\s\S]*?)<\/script>/.exec(sections[env.section] || '');
        return match ? JSON.parse(match[1]) : null;
      });
  }

  // The next single write that moves the cart towards the right gift, or
  // null when the cart is already right.
  function nextWrite(state) {
    if (!state || !state.variant) return null;
    var lines = state.lines || [];
    if (!state.qualifies) {
      if (!lines.length) return null;
      return { path: 'cart/change.js', body: { id: lines[0].key, quantity: 0 } };
    }
    if (!lines.length) {
      if (!state.count) return null;
      var properties = {};
      properties[PROPERTY] = state.title || 'Gift';
      return { path: 'cart/add.js', body: { items: [{ id: state.variant, quantity: 1, properties: properties }] } };
    }
    if (lines.length > 1) return { path: 'cart/change.js', body: { id: lines[lines.length - 1].key, quantity: 0 } };
    if (Number(lines[0].quantity) !== 1) return { path: 'cart/change.js', body: { id: lines[0].key, quantity: 1 } };
    return null;
  }

  function pass(held) {
    holder = held || null;
    var changed = false;
    var limit = 5;

    function step() {
      if (limit-- < 1) return Promise.resolve(changed);
      return readState().then(function (state) {
        var next = nextWrite(state);
        if (!next) return changed;
        changed = true;
        return post(next.path, next.body).then(step);
      });
    }
    var done = function () { holder = null; };
    return step().then(function (result) {
      done();
      return result;
    }, function (error) {
      done();
      throw error;
    });
  }

  // The stamps change the total the gift depends on. Wait until they have
  // settled, so the gift is decided on the stamped cart.
  function afterStamps() {
    var stamps = env.stamps && env.stamps();
    if (!stamps || typeof stamps.idle !== 'function') return Promise.resolve();
    return new Promise(function (resolve) {
      var cap = env.setTimeout(resolve, STAMPS_WAIT);
      stamps.idle().then(function () {
        env.clearTimeout(cap);
        resolve();
      });
    });
  }

  // One pass at a time, after the stamps and holding the shared cart lock.
  // A change that lands during a pass triggers one more.
  function run() {
    if (running) {
      rerun = true;
      return running;
    }
    running = afterStamps().then(function () {
      return lock(pass);
    }).then(function (changed) {
      if (changed) env.refreshCart();
      return changed;
    }, function (error) {
      // The lock let this pass go before it finished: run again, reading
      // the cart first.
      if (error && error.released) rerun = true;
      // A cart request hung past the lock's ceiling. The gift is not urgent:
      // skip this run and try again once that request is done.
      if (error && error.gaveUp) error.settled.then(schedule);
      return false;
    }).then(function (changed) {
      running = null;
      if (rerun) {
        rerun = false;
        schedule();
      }
      return changed;
    });
    return running;
  }

  function schedule() {
    env.clearTimeout(timer);
    timer = env.setTimeout(run, DEBOUNCE);
  }

  function bindPage(win, doc) {
    doc.addEventListener('theme:cartchanged', schedule);
    win.addEventListener('pageshow', function (event) {
      if (event.persisted) schedule();
    });
    if (!('PerformanceObserver' in win)) return;
    try {
      new win.PerformanceObserver(function (list) {
        list.getEntries().forEach(function (entry) {
          if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') return;
          if (entry.name.indexOf(MARKER + '=1') !== -1) return;
          if (/\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/.test(entry.name)) schedule();
        });
      }).observe({ type: 'resource', buffered: false });
    } catch (error) {}
  }

  return {
    run: run,
    schedule: schedule,
    bindPage: bindPage,
    pass: pass,
    nextWrite: nextWrite
  };
});
